import * as React from 'react';
import type { AudienceQfaQuestion } from '@mentimeter/http-clients';
import { Button } from '@mentimeter/ragnar-ui/button';
import { Box } from '@mentimeter/ragnar-ui/box';
import { useTranslations } from 'next-intl';
import { submitQuestion } from '../../api/qfa';
import { gaTrackEvent } from '../../utils/tracker';
import { QfaBrowseView } from './QfaBrowseView';
import { QfaFormContainer } from './QfaFormContainer';
import { QfaForm } from './QfaForm';
import { useQfaStore } from './qfaStore';

export const QfaPanelContent = ({
  voteKey,
  questions,
  activePublicKey,
}: {
  voteKey: string;
  questions: AudienceQfaQuestion[] | undefined;
  activePublicKey?: string | undefined;
}) => {
  const translate = useTranslations('participating.qfa');
  const setAudienceQuestion = useQfaStore((state) => state.setAudienceQuestion);
  const setError = useQfaStore((state) => state.setError);
  const setLoading = useQfaStore((state) => state.setLoading);
  const [showSubmitQuestionView, setShowSubmitQuestionView] =
    React.useState(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const onSubmit = async (question: string) => {
    if (question.length > 0) {
      setLoading(true);
      try {
        const response = await submitQuestion(voteKey, {
          question,
          ...(activePublicKey && { asked_on_question_key: activePublicKey }),
        });

        if (response.status === 201) {
          setAudienceQuestion(response.data);
          setLoading(false);
          gaTrackEvent('QFA', 'Entered QFA');

          timeoutRef.current = setTimeout(() => {
            setShowSubmitQuestionView(false);
          }, 2000);
        }
      } catch (error) {
        setError(error as string);
        setLoading(false);
      }
    }
  };

  const containerRef = React.useRef<HTMLDivElement | null>(null);

  return (
    <Box
      pt="space14"
      pb="space10"
      width="100%"
      height="80vh"
      overflow="auto"
      extend={() => ({
        scrollBehavior: 'smooth',
      })}
      ref={containerRef}
    >
      <Box width="100%" alignItems="stretch">
        {showSubmitQuestionView ? (
          <QfaFormContainer goBack={() => setShowSubmitQuestionView(false)}>
            <QfaForm onSubmit={onSubmit} />
          </QfaFormContainer>
        ) : (
          <>
            <QfaBrowseView />

            <Box
              alignItems="center"
              pt="space5"
              {...(questions?.length && {
                borderColor: 'borderWeak',
                borderTopWidth: '1px',
                borderStyle: 'solid',
              })}
            >
              <Button
                size="large"
                variant="primary"
                onClick={() => {
                  containerRef?.current?.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth',
                  });
                  setShowSubmitQuestionView(true);
                }}
              >
                {translate('ask_new_question_button')}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
